/**
 * Chrome coordinator — single source of truth for the fixed top region.
 *
 * Two responsibilities:
 *
 * 1. Top-bar registry. Every visible fixed bar marked [data-top-bar]
 *    (currently the market-suggestion banner; future announcement bars
 *    follow the same contract) reports its live height through the
 *    --top-bar-height custom property. A ResizeObserver keeps the value
 *    correct across viewport resizes and bar show/hide — no more stale
 *    heights (the old 49px-vs-149px bug).
 *
 * 2. Takeover choreography. On pages with a product-subnav section
 *    (product landing pages), scrolling switches the whole chrome
 *    (banner + header) between two states instead of sliding it 1:1
 *    with scroll:
 *
 *      shown  (scrollY below the chrome height): --chrome-offset is 0px,
 *             --chrome-visible keeps its full value, the sticky subnav
 *             rides glued right below the chrome.
 *      hidden (scrollY past the chrome height):  --chrome-offset jumps
 *             to the full chrome height and --chrome-visible to 0px, so
 *             the subnav pins at the very top. CSS transitions animate
 *             the flip, so the tall header "retracts upward" in one
 *             clean motion instead of stacking with the subnav forever.
 *
 *   The flip is a clean handoff: at the threshold scroll (== chrome
 *   height) the subnav's glued position is exactly the viewport top, so
 *   the two layers never overlap and no z-index whack-a-mole is needed.
 *   A 12px hysteresis on the way back up prevents flicker at the
 *   boundary. [data-chrome-hidden] on <html> mirrors the hidden state
 *   for CSS consumers; [data-chrome-takeover] marks pages whose chrome
 *   participates in a takeover at all.
 *
 *   A second takeover target uses the same flip on a different trigger: the
 *   PDP chapter-tab bar sits below the buy area, so the chrome is retracted
 *   only once that bar has travelled up to the top rather than on the first
 *   scroll — see resolveTakeoverBar and measureTabsArrival.
 *
 *   While the mobile nav drawer is open (ThemeChrome.show()) the chrome
 *   is force-shown, because the drawer's <dialog> lives inside the
 *   transformed header shell and a translated shell would shift it out
 *   of the viewport; ThemeChrome.release() after close restores the
 *   scroll-driven state.
 *
 * Non-takeover pages keep --chrome-offset at 0px and --chrome-visible at
 * its full value (header + bars) — every offset consumer (main padding,
 * cart toast, sticky tabs) reads the same variables.
 */
(function () {
  var root = document.documentElement;

  var takeoverBar = null;
  var rafPending = false;
  var resizeObserver = null;

  /* Static document offset of the PDP chapter-tab bar, cached while the bar is
     not stuck. A pinned sticky element reports its stuck position from both
     getBoundingClientRect and offsetTop, so the arrival point can only be read
     while the bar is free — at load, and again the moment it releases. */
  var tabsArrival = 0;

  /* Hidden-state reveal happens only after the user scrolls back this far
     below the threshold, so a scroll resting at the boundary does not
     flicker the chrome on and off. */
  var REVEAL_HYSTERESIS = 12;

  var chromeHidden = false;
  var forceShow = false;

  /* ---- top-bar registry ---- */

  function measureTopBars() {
    var total = 0;

    document.querySelectorAll('[data-top-bar]').forEach(function (bar) {
      var style = getComputedStyle(bar);
      if (bar.hidden || style.display === 'none' || style.visibility === 'hidden') {
        return;
      }
      total += bar.getBoundingClientRect().height || 0;
    });

    root.style.setProperty('--top-bar-height', total + 'px');
    return total;
  }

  /* --header-height is written as px today. Read px or rem (16px root
     baseline) so a future stylesheet change cannot silently zero the
     measurement; empty or unparsable input falls back to 64px, and a parsed
     0 keeps the previous falsy-fallback behaviour. */
  function readHeaderHeight() {
    var raw = (getComputedStyle(root).getPropertyValue('--header-height') || '').trim();
    var value = parseFloat(raw);
    if (/rem$/.test(raw)) value *= 16;
    return value || 64;
  }

  function measureChromeTotal() {
    var topBarHeight = measureTopBars();
    var headerHeight = readHeaderHeight();

    return headerHeight + topBarHeight;
  }

  /* ---- takeover targets ---- */

  /* The bar whose arrival at the top retracts the chrome on this page.
     Landing pages put their subnav directly beneath the chrome, so it is
     already in position and flipping on the first scroll is correct — the
     original behaviour. PDP chapter tabs sit further down the page, below the
     buy area, so they are handled as an arrival instead: the flip waits until
     the bar has actually travelled up to the top.

     The PDP target is the section wrapper, because that is the element CSS
     makes sticky — the bar inside it is only as tall as its own containing
     block and cannot travel. */
  function resolveTakeoverBar() {
    var subnavBar = document.querySelector('.product-subnav-section');
    if (subnavBar) {
      return { bar: subnavBar, onArrival: false };
    }

    var tabsBar = document.querySelector('.product-sticky-tabs-section');
    if (tabsBar) {
      return { bar: tabsBar, onArrival: true };
    }

    return null;
  }

  /* Document offset at which the chapter-tab bar reaches its glued position.
     Measurable only while the bar is NOT stuck; a pinned bar would otherwise
     overwrite the cached value with a number that moves with the scroll. */
  function measureTabsArrival(bar) {
    var rectTop = bar.getBoundingClientRect().top;
    var gluedTop = parseFloat(getComputedStyle(bar).top) || 0;

    if (rectTop > gluedTop + 0.5) {
      tabsArrival = rectTop + (window.scrollY || 0);
    }

    return tabsArrival;
  }

  /* ---- takeover state ---- */

  function applyChrome(offset, visible) {
    root.style.setProperty('--chrome-offset', offset + 'px');
    root.style.setProperty('--chrome-visible', Math.max(0, visible) + 'px');

    if (offset > 0) {
      root.setAttribute('data-chrome-hidden', '');
    } else {
      root.removeAttribute('data-chrome-hidden');
    }
  }

  function syncChrome() {
    var chromeTotal = measureChromeTotal();
    var takeover = resolveTakeoverBar();
    takeoverBar = takeover ? takeover.bar : null;

    if (!takeoverBar) {
      root.removeAttribute('data-chrome-takeover');
      chromeHidden = false;
      applyChrome(0, chromeTotal);
      return;
    }

    root.setAttribute('data-chrome-takeover', '');

    /* Scroll position at which the bar has reached the top. A subnav bar is
       already glued under the chrome, so it flips as soon as the page scrolls
       (the original threshold). Chapter tabs flip one chrome-height before
       their arrival, the moment they finish gluing into place. */
    var threshold = chromeTotal;
    if (takeover.onArrival) {
      threshold = Math.max(0, measureTabsArrival(takeoverBar) - chromeTotal);
    }

    if (!forceShow) {
      var scrollY = window.scrollY || 0;

      if (!chromeHidden && scrollY >= threshold) {
        chromeHidden = true;
      } else if (chromeHidden && scrollY <= threshold - REVEAL_HYSTERESIS) {
        chromeHidden = false;
      }
    } else {
      chromeHidden = false;
    }

    if (chromeHidden) {
      applyChrome(chromeTotal, 0);
    } else {
      applyChrome(0, chromeTotal);
    }
  }

  /** Force the chrome back into view (nav drawer open) until release(). */
  function showChrome() {
    forceShow = true;
    chromeHidden = false;
    applyChrome(0, measureChromeTotal());
  }

  /** Stop forcing and let scroll position drive the state again. */
  function releaseChrome() {
    forceShow = false;
    syncChrome();
  }

  /* ---- wiring ---- */

  function onScroll() {
    if (rafPending) return;
    rafPending = true;

    requestAnimationFrame(function () {
      rafPending = false;
      syncChrome();
    });
  }

  function onViewportChange() {
    syncChrome();
  }

  /* Observe the CURRENT [data-top-bar] nodes. Reconnects the ResizeObserver
     against the latest DOM so soft-navigation re-renders (new bar nodes) stay
     tracked and --top-bar-height never goes stale. */
  function observeTopBars() {
    if (!window.ResizeObserver) return;

    if (resizeObserver) {
      resizeObserver.disconnect();
    } else {
      resizeObserver = new ResizeObserver(function () {
        syncChrome();
      });
    }

    document.querySelectorAll('[data-top-bar]').forEach(function (bar) {
      resizeObserver.observe(bar);
    });

    /* A soft navigation swaps in a different page, so the cached arrival point
       belongs to a bar that no longer exists. */
    tabsArrival = 0;

    syncChrome();
  }

  if (window.ResizeObserver) {
    observeTopBars();
  }

  document.addEventListener('site:navigation:end', observeTopBars);

  /* Soft navigation commits the chrome island + #MainContent synchronously
     inside the view-transition update callback (site:navigation:commit), so
     the takeover/offset state must re-settle THERE — before the new state
     is captured — not at the trailing end event. Reusing the same reconcile
     keeps it idempotent; the END-driven pass is still needed for traversal
     scroll restores that land after the transition. */
  document.addEventListener('site:navigation:commit', observeTopBars);

  window.ThemeChrome = {
    refresh: syncChrome,
    show: showChrome,
    release: releaseChrome,
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onViewportChange, { passive: true });

  syncChrome();
})();
